import { transferNameOwnership } from "@bonfida/spl-name-service";
import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection, PublicKey, Signer } from "@solana/web3.js";
import { SOL_TLD_AUTHORITY } from "@stepfinance/common";
import { BaseButton, StepTextInput } from "@stepfinance/react";
import type { FC } from "react";
import { useState } from "react";

import { confirmOptions } from "../../constants/transaction";
import { useDomainsContext } from "../../contexts/domains";
import type { CurrentLanguageSourceType } from "../../contexts/language/imports";
import { useLanguageContext } from "../../contexts/language/language";
import type { ConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { useConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { handlePreSendTxnError, sendTransaction } from "../../utils/transactions";
import { formatStringWithPlaceholders, validateAddressInput } from "../../utils/utils";
import type { UserDomain } from "../dashboard/Balances/Balances";

type TransferDomainNotificationText =
  CurrentLanguageSourceType["pages"]["dashboard"]["modules"]["balances"]["transferDomainModal"]["notifications"];

const transferDomainOwnership = async (
  wallet: ConnectedWallet,
  connection: Connection,
  name: string,
  newOwner: PublicKey,
  onTransactionInProgress: () => void,
  notificationText: TransferDomainNotificationText,
) => {
  const transferInstruction = await transferNameOwnership(connection, name, newOwner, undefined, SOL_TLD_AUTHORITY);
  const signers: Signer[] = [];
  return sendTransaction(
    connection,
    wallet,
    [transferInstruction],
    signers,
    true,
    confirmOptions,
    true,
    {
      success: {
        message: notificationText.sent,
        description: formatStringWithPlaceholders(notificationText.youSentDomain, name, newOwner.toBase58()),
      },
    },
    onTransactionInProgress,
  );
};

type SendDomainProps = {
  domain: UserDomain;
  closeModal: () => void;
};

export const SendDomainContent: FC<SendDomainProps> = ({ domain, closeModal }) => {
  const { connection } = useConnection();
  const { refreshDomains } = useDomainsContext();
  const wallet = useConnectedWallet();
  const [toAddressText, setToAddressText] = useState("");
  const [toAddress, setToAddress] = useState<PublicKey | null>(null);
  const [transactionPending, setTransactionPending] = useState(false);

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: {
            balances: { sendTokenContent, transferDomainModal },
          },
        },
      },
    },
  } = useLanguageContext();

  const disableSend = !toAddress || transactionPending;

  let buttonText = common.transfer;
  if (!toAddress) {
    buttonText = common.stepButtonLabels.enterAddress;
  } else if (transactionPending) {
    buttonText = common.stepButtonLabels.approveTransaction;
  }

  return (
    <>
      <DomainDisplayDiv>{domain.name}.sol</DomainDisplayDiv>
      <ToAddressDiv>{sendTokenContent.toAddress}</ToAddressDiv>
      <StepTextInput
        value={toAddressText}
        spellCheck={false}
        variant="filled"
        borderless
        style={{ backgroundColor: "black" }}
        inputSize="small"
        placeholder={sendTokenContent.addressInputPlaceholder}
        onChange={(e) => {
          setToAddressText(e.target.value);
          void validateAddressInput(e.target.value, connection, true).then(setToAddress);
        }}
      />
      <SendButton
        disabled={disableSend}
        onClick={() => {
          if (wallet && toAddress) {
            setTransactionPending(true);
            transferDomainOwnership(
              wallet,
              connection,
              domain.name,
              toAddress,
              closeModal,
              transferDomainModal.notifications,
            )
              .catch((e) => {
                handlePreSendTxnError(e as Error, "Error sending name");
                closeModal();
              })
              .finally(() => {
                setTransactionPending(false);
                refreshDomains();
              });
          }
        }}
      >
        {buttonText}
      </SendButton>
    </>
  );
};

const ToAddressDiv = styled.div`
  margin-top: 30px;
  margin-bottom: 10px;
  color: #b2b2b2;
  font-size: 10px;
  line-height: 22px;
`;

const DomainDisplayDiv = styled.div`
  display: flex;
  padding: 16px 20px;
  align-items: center;
  color: #06d6a0;
  background: #202020;
  font-size: 14px;
  font-weight: 400;
  border: 1px solid rgba(120, 120, 120, 0.15);
  box-sizing: border-box;
  border-radius: 2px;
`;

const SendButton = styled(BaseButton)`
  width: 100%;
  padding: 10px;
  margin-top: 30px;
  font-size: 16px;
  font-weight: 800;
  line-height: 22px;
  height: 60px;
  letter-spacing: 0em;
`;
